import React, { useState, useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { updateCampaignRequestNotesField } from '~/services/campaign';
import { toast } from 'react-hot-toast';

interface EditRequestNotesModalProps {
    isOpen: boolean;
    notes: string;
    campaignRequestId: number;
    onClose: () => void;
    onSave: (updatedNotes: string) => void;
}

const EditRequestNotesModal: React.FC<EditRequestNotesModalProps> = ({
    isOpen,
    notes,
    campaignRequestId,
    onClose,
    onSave,
}) => {
    const [noteContent, setNoteContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setNoteContent(notes || '');
        }
    }, [isOpen, notes]);

    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27) onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', keyHandler);
        }
        return () => document.removeEventListener('keydown', keyHandler);
    }, [isOpen, onClose]);

    const handleSave = async () => {
        if (isSaving) return;

        if (noteContent === (notes || '')) {
            onClose();
            return;
        }

        setIsSaving(true);
        try {
            const response = await updateCampaignRequestNotesField(
                campaignRequestId,
                noteContent
            );

            if (response.ok) {
                toast.success('Request notes updated');
                onSave(noteContent);
                onClose();
            } else {
                const errorData = await response.json().catch(() => ({}));
                toast.error(errorData.detail || 'Failed to update request notes');
            }
        } catch (error) {
            console.error('Error updating request notes:', error);
            toast.error('Failed to update request notes');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75'
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='relative max-h-[80vh] w-full max-w-2xl rounded-lg bg-white shadow-lg'
            >
                <div className='flex items-center justify-between p-6 pb-4'>
                    <h3 className='text-gray-900 text-[28px]/[32px] font-semibold'>
                        Edit Request Notes
                    </h3>
                    <button
                        className='text-gray-400 hover:text-gray-600 transition-colors'
                        onClick={onClose}
                        aria-label='Close modal'
                    >
                        <RxCross2 size={24} />
                    </button>
                </div>

                <div className='max-h-[60vh] overflow-y-auto px-6'>
                    <div className='mb-3'>
                        <label className='block text-xs font-semibold mb-1'>
                            Notes
                        </label>
                        <textarea
                            value={noteContent}
                            onChange={(e) => setNoteContent(e.target.value)}
                            rows={6}
                            className='w-full rounded-md border border-black px-3 py-2 text-sm focus:border-primary focus:outline-none'
                        />
                    </div>
                </div>

                <div className='border-t border-gray-200 p-6'>
                    <div className='flex justify-start gap-3'>
                        <button
                            type='button'
                            onClick={onClose}
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-semibold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        >
                            Cancel
                        </button>
                        <button
                            type='button'
                            onClick={handleSave}
                            disabled={isSaving}
                            className='rounded-full bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-semibold uppercase text-black transition-colors duration-500 hover:bg-yellow disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditRequestNotesModal;
